/**
 * trendCompare.js
 * Presentation-only helper for Trend Indicators.
 *
 * This module performs NO financial calculations. It only compares two
 * already-computed numbers (current vs previous, both produced by
 * buildCumulativeData()/computeHistoricalSnapshot() in buildCostControlData.js)
 * and formats the comparison for display (difference, % change, direction,
 * color). Callers are responsible for sourcing both numbers correctly.
 */

/**
 * Compare a current value to a previous value for display as a trend badge.
 * @param {number} current
 * @param {number} previous
 * @param {object} [opts]
 * @param {boolean} [opts.higherIsBetter=true] — controls color only (e.g. for
 *   VAC/Gross Profit, higher is better = green up; for EAC, lower is often
 *   "better" so pass higherIsBetter:false to flip the color, not the math).
 * @returns {{current:number, previous:number, diff:number, pct:number|null,
 *   direction:'up'|'down'|'flat', arrow:'▲'|'▼'|'►', color:string}}
 */
export function buildTrend(current, previous, opts = {}) {
  const { higherIsBetter = true } = opts
  const c = current || 0
  const p = previous || 0
  const diff = c - p
  const pct = p ? (diff / Math.abs(p)) * 100 : null

  let direction = 'flat'
  if (diff > 0) direction = 'up'
  else if (diff < 0) direction = 'down'

  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '►'

  // Color is a pure presentation choice: which direction counts as
  // "good news" depends on the metric, controlled by higherIsBetter.
  let color = '#6B7280' // flat/neutral gray
  if (direction !== 'flat') {
    const isGood = higherIsBetter ? direction === 'up' : direction === 'down'
    color = isGood ? '#1A7A4A' : '#DC2626'
  }

  // Significance is a presentation threshold on the already-computed pct —
  // not a new calculation, just a label so the UI/narrative can decide
  // whether a movement deserves management attention. Thresholds are
  // deliberately simple and adjustable in one place.
  const absPct = pct === null ? 0 : Math.abs(pct)
  let significance = 'stable'
  if (absPct >= 10) significance = 'significant'
  else if (absPct >= 3) significance = 'moderate'

  return { current: c, previous: p, diff, pct, direction, arrow, color, significance }
}

/**
 * Compare several named metrics at once between two snapshot-shaped objects
 * (e.g. two grandTotals from buildCumulativeData()/computeHistoricalSnapshot()).
 * `fields` maps output key -> { path, higherIsBetter }, where path is a key
 * to read off both `currentSource` and `previousSource`.
 * Still pure presentation — no math beyond buildTrend()'s simple diff/pct.
 */
export function compareSnapshots(currentSource, previousSource, fields) {
  const out = {}
  for (const [key, cfg] of Object.entries(fields)) {
    const path = typeof cfg === 'string' ? cfg : cfg.path
    const higherIsBetter = typeof cfg === 'string' ? true : (cfg.higherIsBetter ?? true)
    out[key] = buildTrend(currentSource?.[path], previousSource?.[path], { higherIsBetter })
  }
  return out
}

/**
 * Translate a trend object (from buildTrend/compareSnapshots) into a short
 * executive-facing sentence for a monetary metric (EAC, Gross Profit, etc.).
 * Pure text formatting — reads only fields already present on `trend`
 * (direction, pct, significance), performs no comparison or calculation.
 * @param {object} trend — a buildTrend() result
 * @param {object} labels — { metric, up, down } short phrases, e.g.
 *   { metric:'Forecast (EAC)', up:'increased', down:'decreased' }
 * @param {function} [formatAmount] — formatter for the diff amount (e.g. fmtM)
 */
export function narrateMonetaryTrend(trend, labels, formatAmount = (n) => n) {
  if (!trend || trend.previous === 0) return null
  const { metric, up = 'increased', down = 'decreased' } = labels
  if (trend.direction === 'flat') return `${metric} is unchanged from last month.`
  const verb = trend.direction === 'up' ? up : down
  const pctText = trend.pct !== null ? ` (${Math.abs(trend.pct).toFixed(1)}%)` : ''
  const attention = trend.significance === 'significant' ? ' — this movement warrants management attention.' : ''
  return `${metric} ${verb} by SAR ${formatAmount(Math.abs(trend.diff))}${pctText} compared to last month${attention}`
}

/**
 * Translate a CPI trend into an executive sentence: improved / deteriorated /
 * remained stable. Pure text formatting over the existing trend object.
 */
export function narrateCpiTrend(trend) {
  if (!trend || trend.previous === 0) return null
  if (trend.direction === 'flat') return 'CPI has remained stable compared to last month.'
  const verb = trend.direction === 'up' ? 'improved' : 'deteriorated'
  const attention = trend.significance === 'significant' ? ' — worth flagging to management.' : ''
  return `CPI has ${verb} from ${trend.previous.toFixed(3)} to ${trend.current.toFixed(3)} compared to last month${attention}`
}
